import { CSSResultGroup, html, LitElement, nothing, TemplateResult } from 'lit'
import { t } from '@cloudsuite/mosaic-shared/translations/translator.js'
import { customElement, property, state } from 'lit/decorators.js'
import { plannedDeliveryDates } from './planned-delivery-dates.style.js'
import { hostStyle } from '@cloudsuite/mosaic-shared/styles/host.style.js'
import { inject } from '@cloudsuite/mosaic-shared/scripts/dependency-injection.js'
import { createEffect } from '@cloudsuite/mosaic-shared/scripts/signals.js'
import {
  PLANNED_DELIVERY_DATES_FETCHER,
  PlannedDeliveryDate,
  PlannedDeliveryDatesFetcher,
  PlannedDeliveryItem
} from 'components/ordering/backorders/api/planned-delivery-dates.js'
import {
  PRODUCT_INFORMATION_FETCHER,
  ProductInformationFetcher
} from 'components/ordering/stores/product/search-product-api.js'
import {
  ProductInfo,
  SEARCH_PRODUCT_STORE,
  SearchProductStore
} from 'components/ordering/stores/product/search-product.js'
import { SALE_DETAIL_PAGES_STORE, SaleDetailPagesStore } from 'components/ordering/sale/store/sale-detail-pages.js'


const translation = {
  noBackorders: () => t('no_backorders', { ns: 'csc-planned-delivery-dates', default: 'You have no backorders' }),
  expectedDeliveryDate: () => t('expected_delivery_date', { ns: 'csc-planned-delivery-dates', default: 'Expected delivery date' }),
  lineCount: () => t('line_count', { ns: 'csc-planned-delivery-dates', default: 'Products' }),
  quantity: () => t('quantity', { ns: 'csc-planned-delivery-dates', default: 'Quantity' }),
  orderDate: () => t('order_date', { ns: 'csc-planned-delivery-dates', default: 'Order date' }),
  saleDetail: () => t('sale_detail', { ns: 'csc-planned-delivery-dates', default: 'Order details' }),
  viewOrder: () => t('view_order', { ns: 'csc-planned-delivery-dates', default: 'View order' }),
  productUnavailable: () => t('product_unavailable', { ns: 'csc-planned-delivery-dates', default: 'Product information unavailable' }),
} as const


@customElement('csc-planned-delivery-dates')
export class CSCPlannedDeliveryDates extends LitElement {
  static override styles: CSSResultGroup = [hostStyle, plannedDeliveryDates]

  @inject(SEARCH_PRODUCT_STORE)
  private searchProductStore!: SearchProductStore

  @inject(SALE_DETAIL_PAGES_STORE)
  private saleDetailPagesStore!: SaleDetailPagesStore

  @inject(PLANNED_DELIVERY_DATES_FETCHER)
  plannedDeliveryDatesFetcher!: PlannedDeliveryDatesFetcher

  @inject(PRODUCT_INFORMATION_FETCHER)
  productInformationFetcher!: ProductInformationFetcher

  @state()
  products: Map<number, ProductInfo> = new Map()

  @state()
  detailPages: Map<string, string> = new Map()

  @state()
  plannedDeliveryDates: PlannedDeliveryDate[] = []

  @property({ attribute: 'user-id', type: Number })
  userId?: number

  @property({ attribute: 'party-id', type: Number })
  partyId?: number

  @property({ attribute: 'shop-id', type: Number })
  shopId?: number

  @property({ attribute: 'headless-endpoint' })
  headlessEndpoint?: string

  @property({ attribute: 'sale-detail-pages-endpoint' })
  fetchSaleDetailPagesEndpoint?: string

  @property({ attribute: 'locale', type: String })
  locale: string = 'en-US'

  @property({ attribute: 'date-style', type: String })
  dateStyle: 'full' | 'long' | 'medium' | 'short' = 'short'

  @property({ attribute: 'historical-days', type: Number })
  historicalDays: number = 3

  @state()
  productsLoaded = false

  constructor() {
    super()

    createEffect(() => {
      this.products = this.searchProductStore.products()
      this.detailPages = this.saleDetailPagesStore.pages()
    })
  }

  override async connectedCallback() {
    super.connectedCallback()
    if (this.headlessEndpoint === undefined || this.fetchSaleDetailPagesEndpoint === undefined || this.shopId === undefined || this.partyId === undefined || this.userId === undefined) {
      throw new Error('missing attribute(s)')
    }
    this.searchProductStore.initialize(this.headlessEndpoint)
    this.saleDetailPagesStore.initialize(this.fetchSaleDetailPagesEndpoint)
    this.plannedDeliveryDates = await this.plannedDeliveryDatesFetcher.getPlannedDeliveryDates(
      this.shopId,
      this.partyId,
      this.userId,
      this.historicalDays,
    )
    const allPlannedItems: PlannedDeliveryItem[] = this.plannedDeliveryDates.flatMap(
      (deliveryDate: PlannedDeliveryDate) => deliveryDate.items
    )
    await this.mapDeliveryDateItemsToPages(allPlannedItems)

    // Here all products for every date are fetched on component load, while just one date is actually shown initially.
    // An obvious optimization is to only fetch product info for the show date, and fetch more on user interaction.
    // This will also help with possible rate limiting problems on the product api.
    for (const deliveryDate of this.plannedDeliveryDates) {
      await this.mapDeliveryDateItemsToProducts(deliveryDate)
    }
    this.productsLoaded = true
  }

  deduplicateProducts = (productIds: number[]) => {
    return Array.from(new Set(productIds))
  }

  mapDeliveryDateItemsToProducts = async (deliveryDate: PlannedDeliveryDate) => {
    const productIds = deliveryDate.items.map(item => item.productId)
    await this.searchProductStore.getProducts(this.deduplicateProducts(productIds))
  }

  mapDeliveryDateItemsToPages = async (deliveryItems: PlannedDeliveryItem[]) => {
    const saleTokens = deliveryItems.map(item => item.saleToken)
    await this.saleDetailPagesStore.getPages(Array.from(new Set(saleTokens)))
  }

  override render(): TemplateResult {
    if (this.plannedDeliveryDates.length == 0) {
      return html`
        <cse-label>
          ${translation.noBackorders()}
        </cse-label>`
    }
    return html`
      <cse-group direction="column" row-gap="06">
        ${this.plannedDeliveryDates.map(this.renderPlannedDeliveryDate)}
      </cse-group>
    `
  }

  private renderPlannedDeliveryDate = (plannedDeliveryDate: PlannedDeliveryDate, index: number) => {
    return html`
      <cse-accordion .open="${index === 0}" variant="default">
        <div slot="title">
          ${this.renderPlannedDeliveryDateTitle(plannedDeliveryDate)}
        </div>

        <div slot="content">
          ${this.renderPlannedDeliveryDateItems(plannedDeliveryDate)}
        </div>
      </cse-accordion>
    `
  }

  private renderPlannedDeliveryDateTitle = (plannedDeliveryDate: PlannedDeliveryDate) => {
    return html`
      <cse-group direction="row" justify="start" alignment="normal" column-gap="08" data-test="title-group">
        <cse-caption-description alignment="left" caption-variant="caption-s" description-variant="body">
          <span slot="caption">
            ${translation.expectedDeliveryDate()}
          </span>
          ${Intl.DateTimeFormat(this.locale, { dateStyle: this.dateStyle }).format(plannedDeliveryDate.date)}
        </cse-caption-description>
        
        <cse-caption-description alignment="left" caption-variant="caption-s" description-variant="body">
          <span slot="caption">
            ${translation.lineCount()}
          </span>
          ${plannedDeliveryDate.items.length}
        </cse-caption-description>
      </cse-group>
    `
  }

  private renderPlannedDeliveryDateItems = (plannedDeliveryDate: PlannedDeliveryDate) => {
    return html`
      <cse-paper variant="secondary" size="default">
        ${plannedDeliveryDate.items.map(this.renderPlannedDeliveryDateItem)}
      </cse-paper>
    `
  }

  private renderPlannedDeliveryDateItem = (item: PlannedDeliveryItem) => {
    const product = this.products.get(item.productId)
    if (product === undefined) {
      if (!this.productsLoaded) return nothing
      return this.renderProductUnavailable(item)
    }

    return html`
      <cse-paper variant="primary" bordered="yay" size="default">
        <cse-group direction="column" row-gap="06">

          <csc-product-preview
            image-source="${product.img_url}"
            product-name="${product.name}"
            product-id="${product.code}">
          </csc-product-preview>

          <cse-paper variant="secondary" size="default" data-test="delivery-details">
            <cse-group direction="row" justify="space-between" alignment="normal" column-gap="06">

              <cse-caption-description alignment="left" caption-variant="caption-s" description-variant="body">
                <span slot="caption">
                  ${translation.quantity()}
                </span>
                ${item.quantity}
              </cse-caption-description>

              <cse-caption-description alignment="left" caption-variant="caption-s" description-variant="body" data-test="delivery-date">
                <span slot="caption">
                  ${translation.orderDate()}
                </span>
                ${Intl.DateTimeFormat(this.locale, { dateStyle: this.dateStyle }).format(item.saleDate)}
              </cse-caption-description>

              <cse-caption-description alignment="left" caption-variant="caption-s" description-variant="body">
                <span slot="caption">
                  ${translation.saleDetail()}
                </span>
                <cse-link url="${this.detailPages.get(item.saleToken)}">
                  ${translation.viewOrder()}
                </cse-link>
              </cse-caption-description>

            </cse-group>
          </cse-paper>

        </cse-group>
      </cse-paper>
    `
  }

  private renderProductUnavailable = (item: PlannedDeliveryItem) => {
    return html`
      <cse-paper variant="primary" bordered="yay" size="default">
        <cse-group direction="column" row-gap="06">

          <cse-label>${translation.productUnavailable()}</cse-label>

          <cse-paper variant="secondary" size="default" data-test="delivery-details">
            <cse-group direction="row" justify="space-between" alignment="normal" column-gap="06">

              <cse-caption-description alignment="left" caption-variant="caption-s" description-variant="body">
                <span slot="caption">
                  ${translation.quantity()}
                </span>
                ${item.quantity}
              </cse-caption-description>

              <cse-caption-description alignment="left" caption-variant="caption-s" description-variant="body" data-test="delivery-date">
                <span slot="caption">
                  ${translation.orderDate()}
                </span>
                ${Intl.DateTimeFormat(this.locale, { dateStyle: this.dateStyle }).format(item.saleDate)}
              </cse-caption-description>

              <cse-caption-description alignment="left" caption-variant="caption-s" description-variant="body">
                <span slot="caption">
                  ${translation.saleDetail()}
                </span>
                <cse-link url="${this.detailPages.get(item.saleToken)}">
                  ${translation.viewOrder()}
                </cse-link>
              </cse-caption-description>

            </cse-group>
          </cse-paper>

        </cse-group>
      </cse-paper>
    `
  }
}
